/**
 * Every text of the app shell (Russian). Players are kids: short sentences, simple words,
 * a friendly tone. Components never hard-code user-visible strings; they import `ru`.
 *
 * Profiles have no gender, so texts about a player never use gendered past tense or short
 * adjectives («готов», «вышел», «зашёл»): present tense, nouns and phrases like «на старте».
 */
import type { ColorId, Difficulty, ErrorCode, RaceFormat, RoomPhase } from '@cube-rush/shared';
import type { LayoutPreference } from '../game/contract';
import type { PadStyle } from '../input/pads';
import type { LoginFailure, StatsFailure } from '../net/api';
import type { ConnectionStatus } from '../net/types';
import type { Holiday } from '../season/season';
import { pluralRu } from '../util/format';

const levelsWord = (n: number) => `${n} ${pluralRu(n, 'уровень', 'уровня', 'уровней')}`;

export const ru = {
  appName: 'Cube Rush',
  tagline: 'Гонка кубиков по сети',

  common: {
    close: 'Закрыть',
    you: 'ты',
    done: 'Готово!',
  },

  login: {
    nameLabel: 'Как тебя зовут?',
    namePlaceholder: 'Твоё имя',
    passwordLabel: 'Пароль',
    passwordPlaceholder: 'Секретное слово',
    showPassword: 'Показать пароль',
    hidePassword: 'Скрыть пароль',
    /** Computers only: a Mac switches a password field to English (the server copes). */
    passwordHint: 'На компьютере раскладку можно не переключать: набирай пароль как по-русски.',
    submit: 'Играть!',
    submitting: 'Заходим…',
    sessionExpired: 'Нужно войти ещё раз',
    errors: {
      no_name: 'Напиши своё имя',
      no_password: 'Введи пароль',
      wrong_password: 'Пароль не подошёл. Попробуй ещё раз!',
      rate_limited: 'Слишком много попыток — подожди минутку',
      bad_request: 'Что-то не так с именем — попробуй другое',
      unreachable: 'Сервер не отвечает — проверь интернет',
      server: 'На сервере что-то сломалось — попробуй чуть позже',
    } satisfies Record<LoginFailure | 'no_name' | 'no_password', string>,
  },

  profile: {
    title: 'Твой персонаж',
    nameLabel: 'Имя',
    tabs: {
      avatar: 'Аватар',
      icon: 'Кубик',
      colors: 'Цвета',
      trail: 'След',
    },
    primaryColor: 'Основной цвет',
    secondaryColor: 'Второй цвет',
    randomize: 'Случайный',
    save: 'Готово!',
    cancel: 'Отменить изменения',
    nameRequired: 'Сначала напиши имя',
    preview: 'Так выглядит твой кубик',
    colorNames: {
      lime: 'Салатовый',
      green: 'Зелёный',
      cyan: 'Бирюзовый',
      sky: 'Голубой',
      blue: 'Синий',
      purple: 'Фиолетовый',
      magenta: 'Пурпурный',
      pink: 'Розовый',
      red: 'Красный',
      orange: 'Оранжевый',
      yellow: 'Жёлтый',
      gold: 'Золотой',
      white: 'Белый',
      gray: 'Серый',
      black: 'Чёрный',
      brown: 'Коричневый',
    } satisfies Record<ColorId, string>,
  },

  room: {
    defaultTitle: 'Комната',
    rooms: 'Комнаты',
    invite: 'Пригласить',
    character: 'Персонаж',
    settings: 'Настройки',
    install: 'Установить',
    ready: 'Я на старте!',
    readyActive: 'На старте! ✔',
    start: 'Старт!',
    watch: 'Смотреть гонку',
    results: 'Итоги',
    host: 'Запускает гонку',
    readyBadge: 'На старте',
    notReadyBadge: 'Ждём',
    offlineBadge: 'Нет связи',
    spectatorBadge: 'Смотрит',
    micOn: 'Микрофон включён',
    micOff: 'Микрофон выключен',
    speaking: 'Говорит',
    hostHint: 'Ты запускаешь гонку: жми «Старт!», когда все соберутся',
    guestHint: 'Гонка начнётся, когда все нажмут «Я на старте!»',
    levelBadge: (level: number, total: number) => `Уровень ${level}/${total}`,
    // A race against the clock has no last level to count to.
    levelBadgeOpen: (level: number) => `Уровень ${level}`,
    finishedBadge: 'Финиш!',
    quitBadge: 'Не в гонке',
    noRoom: 'Ищем комнату…',
    // No connection for a while since the app opened (the computer with the game asleep).
    noServer: 'Сервер не отвечает — проверь интернет или позови взрослого',
    membersLabel: 'Игроки в комнате',
    readyCount: (ready: number, total: number) => `На старте: ${ready} из ${total}`,
    phase: {
      lobby: 'Ждём игроков',
      countdown: 'Гонка начинается!',
      race: 'Идёт гонка',
      results: 'Гонка закончилась',
    } satisfies Record<RoomPhase, string>,
  },

  /** The race the room runs: its settings, the card in the room and the dialog. */
  race: {
    title: 'Гонка',
    // Button labels of the card, followed by the race («Настроить гонку: 10 уровней, Легко»).
    edit: 'Настроить гонку',
    view: 'Какая будет гонка',
    formatLabel: 'Как соревнуемся',
    formats: { levels: 'По уровням', time: 'На время' } satisfies Record<RaceFormat, string>,
    formatIcons: { levels: '🏁', time: '⏱️' } satisfies Record<RaceFormat, string>,
    formatHints: {
      levels: 'Кто быстрее пройдёт все уровни',
      time: 'Кто больше уровней пройдёт, пока идёт время',
    } satisfies Record<RaceFormat, string>,
    levelsLabel: 'Сколько уровней',
    timeLabel: 'Сколько времени',
    difficultyLabel: 'Сложность',
    difficulties: {
      easy: 'Легко',
      medium: 'Средне',
      hard: 'Сложно',
      rising: 'Нарастающая',
    } satisfies Record<Difficulty, string>,
    difficultyIcons: {
      easy: '🙂',
      medium: '😎',
      hard: '🔥',
      rising: '📈',
    } satisfies Record<Difficulty, string>,
    // What makes a level hard is how many jumps come in a row and how little calm road there is.
    difficultyHints: {
      easy: 'Прыжки по одному, между ними — спокойная дорога',
      medium: 'Короткие серии прыжков, сферы, корабль и шар',
      hard: 'Много прыжков подряд — решать надо быстро!',
      rising: 'Начинаем легко, каждый уровень сложнее прошлого',
    } satisfies Record<Difficulty, string>,
    levels: levelsWord,
    minutes: (n: number) => `${n} ${pluralRu(n, 'минута', 'минуты', 'минут')}`,
    minutesShort: (n: number) => `${n} мин`,
    // Lengths that are not whole minutes (a server set up for tests).
    seconds: (n: number) => `${n} с`,
    hostNote: (name: string) => `Гонку настраивает 👑 ${name}`,
    // The host looking at the race while it runs.
    lockedNote: 'Пока идёт гонка, её не поменять',
    changed: (summary: string) => `Новая гонка: ${summary}`,
    preparing: 'Готовим уровни…',
    timeLeft: (time: string) => `осталось ${time}`,
  },

  voice: {
    enable: 'Включить голос',
    mute: 'Выключить микрофон',
    unmute: 'Включить микрофон',
    micOn: 'Тебя слышно',
    micOff: 'Тебя не слышно',
    unavailable: 'Микрофон недоступен',
    unavailableHint: 'Может, он занят другой программой — друзей ты всё равно слышишь',
    denied: 'Нет доступа к микрофону',
    deniedHint: 'Разреши микрофон в настройках браузера — друзей ты всё равно слышишь',
    // Mostly the browser built into a messenger app: no microphone (often no voice at all).
    unsupported: 'Этот браузер не даёт микрофон — открой игру в Safari или Chrome',
    requesting: 'Включаем микрофон…',
    requestingHint: 'Если браузер спросит, нажми «Разрешить»',
    retry: 'Попробуй ещё раз',
    headphonesTip: 'Совет: в наушниках не будет эха 🎧',
    connecting: 'Ловим голос…',
    lost: 'Не слышно',
    peerVolume: (name: string, percent: number) => `${name}: громкость ${percent}%`,
  },

  rooms: {
    title: 'Комнаты',
    empty: 'Пока других комнат нет — создай свою!',
    join: 'Войти',
    current: 'Ты здесь',
    full: 'Мест нет',
    createTitle: 'Новая комната',
    createPlaceholder: 'Название',
    createLabel: 'Название новой комнаты',
    create: 'Создать',
    leave: 'Вернуться в общую комнату',
    members: (n: number, max: number) => `${n}/${max}`,
  },

  invite: {
    title: 'Пригласить друга',
    hint: 'Пусть друг наведёт камеру телефона на код или откроет ссылку. А пароль скажи ему по секрету!',
    copy: 'Скопировать ссылку',
    copied: 'Ссылка скопирована!',
    copyFailed: 'Не получилось скопировать — выдели ссылку вручную',
    share: 'Отправить',
    shareText: 'Залетай в Cube Rush — погоняем!',
    qrLabel: 'QR-код со ссылкой на игру',
  },

  install: {
    guideTitle: 'Игра на экране «Домой»',
    guideSteps: [
      // Newer iOS hides «Поделиться» behind «•••» (next to the address bar).
      'Нажми «Поделиться» — квадрат со стрелкой вверх (не видно его — сначала нажми «•••»)',
      'Выбери «На экран „Домой“»',
      'Нажми «Добавить» — готово!',
    ],
    // iOS keeps the home-screen app's storage apart from Safari's: one more login there.
    guideNote:
      'Потом запускай игру с экрана «Домой», как обычное приложение. Там войди ещё раз — с тем же паролем.',
    installed: 'Игра установлена!',
  },

  settings: {
    title: 'Настройки',
    music: 'Музыка',
    sfx: 'Звуки',
    voices: 'Голоса друзей',
    layout: 'Экран',
    layoutOptions: {
      auto: 'Авто',
      single: 'Один',
      split: 'Окошки',
    } satisfies Record<LayoutPreference, string>,
    layoutHints: {
      auto: 'На телефоне — один экран с тенями, на планшете и компьютере — окошки соперников',
      single: 'Всегда один экран, соперники видны полупрозрачными тенями',
      split: 'Экран делится: сверху твоя игра, ниже — окошки соперников',
    } satisfies Record<LayoutPreference, string>,
    ghosts: 'Тени соперников',
    haptics: 'Вибрация',
    logout: 'Выйти из игры',
    logoutConfirm: 'Точно выйти? Нажми ещё раз',
  },

  results: {
    title: 'Итоги гонки',
    again: 'Ещё раз!',
    toRoom: 'В комнату',
    place: 'Место',
    player: 'Игрок',
    time: 'Время',
    levels: 'Уровни',
    deaths: 'Падения',
    notFinished: '—',
    quit: 'не в гонке',
    levelsOf: (done: number, total: number) => `${done}/${total}`,
    // The score of a race against the clock (the podium's main line).
    levelsDone: levelsWord,
  },

  /** The statistics screen (from the room's tools): every player's numbers and the family's. */
  stats: {
    title: 'Статистика',
    // The room's tool button.
    open: 'Статистика',
    back: 'В комнату',
    refresh: 'Обновить',
    loading: 'Считаем…',
    failed: {
      unreachable: 'Сервер не отвечает — проверь интернет',
      server: 'Статистика не загрузилась — попробуй ещё раз',
      unauthorized: 'Нужно войти ещё раз',
    } satisfies Record<StatsFailure, string>,
    retry: 'Ещё раз',
    empty: 'Пока ни одной гонки. Сыграйте — и здесь появятся ваши рекорды!',
    you: 'ты',
    // The two kids side by side.
    versus: 'Кто кого',
    together: (n: number) => `${n} ${pluralRu(n, 'гонка', 'гонки', 'гонок')} вместе`,
    ahead: 'Впереди',
    rows: {
      wins: 'Победы',
      races: 'Гонки',
      levels: 'Пройдено уровней',
      deaths: 'Падения',
      time: 'Время в игре',
      bestWins: 'Побед подряд',
    },
    tiles: {
      races: 'гонок',
      wins: 'побед',
      podiums: 'на пьедестале',
      levels: 'уровней',
      deaths: 'падений',
      time: 'в игре',
    },
    favourite: 'Любимый кубик',
    favouriteRaces: (n: number) => `${n} ${pluralRu(n, 'гонка', 'гонки', 'гонок')}`,
    daysInRow: (n: number) => `${n} ${pluralRu(n, 'день', 'дня', 'дней')} подряд`,
    winsInRow: (n: number) => `${n} ${pluralRu(n, 'победа', 'победы', 'побед')} подряд`,
    record: (n: number) => `рекорд: ${n}`,
    noStreak: 'Сыграй сегодня — начнётся серия!',
    bests: 'Рекорды',
    noBests: 'Пройди гонку до конца — здесь будет рекорд',
    recent: 'Последние гонки',
    showAll: (n: number) => `Показать все (${n})`,
    showLess: 'Свернуть',
    solo: 'один на трассе',
    // A race given up (or dropped out of) before the end.
    quit: 'без финиша',
    racers: (n: number) => `${n} ${pluralRu(n, 'игрок', 'игрока', 'игроков')}`,
    today: 'сегодня',
    yesterday: 'вчера',
    family: 'Вся семья',
    familyTiles: {
      races: 'гонок',
      time: 'в игре',
      levels: 'уровней',
      deaths: 'падений',
      days: 'дней с гонками',
    },
    since: (date: string) => `Играем с ${date}`,
    // No-break spaces: a narrow tile wraps between «3 ч» and «41 мин», never inside them.
    hours: (h: number, m: number) => (m > 0 ? `${h}\u00a0ч ${m}\u00a0мин` : `${h}\u00a0ч`),
    minutes: (m: number) => `${m}\u00a0мин`,
    lessThanMinute: 'меньше минуты',
    // The same in the narrow columns of «Кто кого».
    lessThanMinuteShort: '<\u00a01\u00a0мин',
  },

  connection: {
    banner: {
      connecting: 'Подключаемся…',
      reconnecting: 'Переподключаемся…',
    } satisfies Partial<Record<ConnectionStatus, string>>,
    offline: 'Нет связи с сервером — попробуй чуть позже',
  },

  replaced: {
    title: 'Игра открыта в другом месте',
    text: 'Ты играешь на другом устройстве или в другой вкладке. Играть можно только в одном месте сразу.',
    button: 'Играть здесь',
  },

  update: {
    title: 'Вышла новая версия!',
    text: 'Обнови игру, чтобы играть с друзьями.',
    button: 'Обновить',
    updating: 'Обновляемся…',
    applying: 'Ставим новую версию игры…',
  },

  errors: {
    room_not_found: 'Такой комнаты больше нет',
    room_full: 'В этой комнате нет мест',
    too_many_rooms: 'Комнат слишком много — зайди в одну из готовых',
    not_allowed: 'Сейчас так нельзя',
    rate_limited: 'Не так быстро — подожди чуть-чуть',
    bad_message: 'Что-то пошло не так',
    internal: 'На сервере что-то сломалось',
    unauthorized: 'Нужно войти ещё раз',
    version_mismatch: 'Вышла новая версия',
  } satisfies Record<ErrorCode, string>,

  toasts: {
    roomClosed: 'Комнату закрыли — ты снова в общей комнате',
    // The server let the race go on without a racer who was away too long (app in the
    // background, phone locked): their game turns into watching.
    droppedFromRace: 'Тебя долго не было — гонка идёт без тебя. Можно смотреть!',
  },

  /** A gamepad (джойстик): what its first press says, and the names of its buttons. */
  gamepad: {
    // A toast at the first press of a page load, with what is what on this pad.
    connected: (legend: string) => `🎮 Джойстик подключён! ${legend}`,
    // Xbox letters, or PlayStation symbols on the buttons in the same places.
    legend: {
      xbox: 'A — прыжок, X и Y — бусты, Start — меню',
      playstation: '✕ — прыжок, □ и △ — бусты, Options — меню',
    } satisfies Record<PadStyle, string>,
    // Under the countdown, instead of the keys.
    hint: {
      xbox: 'A — прыжок, X и Y — бусты',
      playstation: '✕ — прыжок, □ и △ — бусты',
    } satisfies Record<PadStyle, string>,
    // The jump button, in the hints of whole ball and wave levels.
    jumpButton: { xbox: 'A', playstation: '✕' } satisfies Record<PadStyle, string>,
    // On the two boost buttons.
    boostKeys: {
      xbox: ['X', 'Y'],
      playstation: ['□', '△'],
    } satisfies Record<PadStyle, readonly [string, string]>,
  },

  /** In-game texts (the game engine: HUD, overlays, notices). */
  game: {
    getReady: 'Приготовься!',
    go: 'Старт!',
    tapToJump: 'Жми на экран — прыжок!',
    keysHint: 'Пробел — прыжок, 1 и 2 — бусты',
    level: 'Уровень',
    levelOf: (level: number, total: number) => `${level}/${total}`,
    levelShort: (level: number) => `Ур. ${level}`,
    attempt: (attempt: number) => `Попытка ${attempt}`,
    raceTime: 'Время гонки',
    // A race against the clock: the timer counts down, the race ends for everybody at once.
    timeLeft: 'Осталось времени',
    timeUp: 'Время вышло!',
    levelsDone: (n: number) => `Пройдено уровней: ${n}`,
    waitingResults: 'Ждём итоги…',
    // What the race is about, under the countdown («за 5 минут», «Пройди 10 уровней»).
    goalTime: (length: string) => `Кто больше уровней за ${length}?`,
    goalLevels: (levels: string) => `Пройди ${levels} быстрее всех!`,
    goalMinutes: (n: number) => `${n} ${pluralRu(n, 'минуту', 'минуты', 'минут')}`,
    goalSeconds: (n: number) => `${n} ${pluralRu(n, 'секунду', 'секунды', 'секунд')}`,
    raceProgress: 'Кто где в гонке',
    levelComplete: (level: number) => `Уровень ${level} пройден!`,
    // A whole level as the ball, the wave, the robot or the spider: its banner at the level start,
    // the hint under it the first time in a race (touch, keyboard or the gamepad's jump button).
    // The robot and the spider in cube levels: their banner with the hint as their portal comes
    // up, the first time in a race (once in the race either way).
    modeTitle: {
      ball: 'Шарик!',
      wave: 'Стрелочка!',
      robot: 'Робот!',
      spider: 'Паук!',
      boss: 'Босс!',
    },
    modeHint: {
      ball: {
        touch: 'Тапни — шарик перекатится на другую сторону',
        keys: 'Пробел — шарик перекатится на другую сторону',
        pad: (button: string) => `${button} — шарик перекатится на другую сторону`,
      },
      wave: {
        touch: 'Держи — вверх, отпусти — вниз',
        keys: 'Держи пробел — вверх, отпусти — вниз',
        pad: (button: string) => `Держи ${button} — вверх, отпусти — вниз`,
      },
      robot: {
        touch: 'Держи дольше — прыгнешь выше',
        keys: 'Держи пробел дольше — прыгнешь выше',
        pad: (button: string) => `Держи ${button} дольше — прыгнешь выше`,
      },
      spider: {
        touch: 'Тапни — перескочит на потолок и обратно',
        keys: 'Пробел — перескочит на потолок и обратно',
        pad: (button: string) => `${button} — перескочит на потолок и обратно`,
      },
    },
    // The boss level's banner: who chases you, and how to get away (any controls: «прыгай»). The
    // robot boss is «Робот-великан»: «Робот!» is the cube's own robot mode.
    bossHint: {
      dragon: 'Дракон гонится! Огонь внизу — прыгай, сверху — не прыгай',
      robot: 'Робот-великан гонится! Внизу — прыгай, наверху — не прыгай',
    },
    finish: 'Финиш!',
    yourTime: 'Твоё время',
    place: (place: number) => `${place} место`,
    waiting: 'Ждём остальных…',
    allFinished: 'Все на финише!',
    menu: 'Меню',
    menuNote: 'Гонка не на паузе — время идёт!',
    resume: 'Продолжить',
    settings: 'Настройки',
    giveUp: 'Сдаться',
    // The button asks the question with the hint below it (one line each, even on phones).
    giveUpConfirm: 'Точно сдаться?',
    giveUpAgain: 'Нажми ещё раз',
    // Against the clock stopping keeps the levels done: it is not giving up.
    stop: 'Закончить',
    stopConfirm: 'Точно закончить?',
    leave: 'Выйти',
    boostSlot: (slot: number, name: string | null) =>
      name ? `Буст ${slot}: ${name}` : `Буст ${slot}: пусто`,
    boostActive: (name: string) => `Работает: ${name}`,
    rotateTitle: 'Поверни телефон',
    rotateText: 'Боком экран шире — видно, что впереди',
    // A phone that does not turn has its rotation lock on (iOS ignores the app's orientation).
    // Android phones name that switch differently (Samsung: «Книжная» while it is off).
    rotateLocked: 'Не поворачивается?',
    rotateUnlock: {
      ios: 'Открой Пункт управления и нажми на замок со стрелкой',
      android: 'Проведи пальцем от верха экрана вниз и включи автоповорот',
    },
    watching: 'Смотришь гонку',
    watchingName: (name: string) => `Смотришь: ${name}`,
    previous: 'Предыдущий игрок',
    next: 'Следующий игрок',
    you: 'Ты',
    offline: 'нет связи',
    finishedLabel: '🏁 финиш',
    quit: 'не в гонке',
    noticeLevel: (level: number) => `уровень ${level} пройден!`,
    noticeFinish: 'на финише!',
  },

  /** Holiday seasons (Halloween, winter): their switch in the settings. */
  season: {
    // Shown only while a holiday is on (out of season the switch would do nothing), and for
    // that holiday only: the next one asks again.
    toggle: 'Праздничное оформление',
    icons: { halloween: '🎃', winter: '🎄' } satisfies Record<Holiday, string>,
  },
} as const;
